import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import API from "../../../util/api";
import Report1 from "../../../assets/image/report1.png"
import { showErrorModal } from "../../../util/general";

// Spinner animation
const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Full‐screen loading overlay
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// Spinner circle
const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 36px; height: 36px;
  animation: ${spin} 1s linear infinite;
`;

// Container and styling
const Container = styled.div.attrs({ id: "bigbox" })`
  background: #f8f8f8;
  padding: 1.5em;
`;

const Heading = styled.h1`
  margin: 0 0 0.5em;
  color: ${({ colors }) => colors.labelText};
`;

const SectionsLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const Section = styled.div`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  text-align: center;
  flex: 1 1 ${({ width }) => `${width}px`};
  min-width: 180px;
  max-width: ${({ width }) => `${width}px`};
  margin-top: 10px;
`;

const PDFLink = styled.a`
  display: block;
  font-size: 13px;
  color: #2c4960;
  text-decoration: none;
  margin-bottom: 0.5em;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Shadow = styled.div`
  box-shadow: 2px 5px 2px rgba(0,0,0,0.05);
  border-radius: 5px;
  margin-top: 10px;
  margin-left: 30px;
  width: 160px;
`;

const ShadowContent = styled.div`
  padding: 10px;
`;

const ReportImage = styled.img`
  display: block;
  width: 150px;
  height: 194px;
  margin: 0 auto;
  cursor: pointer;
`;

const CSVSection = styled(Section)`
  text-align: left;
`;

const CSVLabel = styled.div`
  font-size: 13px;
`;

const CSVList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 0.5em;

  li {
    margin-bottom: 0.5em;
    &:before {
      content: "⬇️";
      margin-right: 0.5em;
    }
  }
  a {
    color: #2c4960;
    text-decoration: none;
    cursor: pointer;
    &:hover { text-decoration: underline; }
  }
`;

const OverlayText = styled.div`
  margin-top: 1em;
  color: white;
  font-size: 1.2em;
  text-align: center;
`;

export default function ComplianceDocReports({ colors }) {
  const [loading, setLoading] = useState(false);
  const subscription = useSelector((s) => s.auth.selected_subscriptions) || {};
  const invoiceId = subscription.invoice_id;

  // Unified downloader for PDF or CSV
  const downloadFile = async (apiFn, defaultName) => {
    if (!invoiceId) {
      showErrorModal("No subscription selected.");
      return;
    }
    setLoading(true);
    try {
      const { blob, filename } = await apiFn({ invoice_id: invoiceId });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      showErrorModal(`Unable to download report: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <Spinner colors={colors} />
          <OverlayText>Requesting report… please wait</OverlayText>
        </LoadingOverlay>
      )}

      <Container colors={colors}>
        <Heading colors={colors}>
          Compliance Documentation Reports
        </Heading>
        <SectionsLayout>
          <Section width={200}>
            <PDFLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                downloadFile(
                  API.downloadComplianceDocReport,
                  `ImplementationReport_${invoiceId}.pdf`
                );
              }}
            >
              PDF
            </PDFLink>
            <Shadow>
              <ShadowContent>
                <ReportImage
                  src={Report1}
                  alt="Implementation Report"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadComplianceDocReport,
                      `ImplementationReport_${invoiceId}.pdf`
                    );
                  }}
                />
              </ShadowContent>
            </Shadow>
          </Section>

          <CSVSection width={300}>
            <CSVLabel>CSV</CSVLabel>
            <br />
            <br />
            <CSVList>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadParticipatingSchoolsCSV,
                      `ParticipatingSchools_${invoiceId}.csv`
                    );
                  }}
                >
                  Participating Schools
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadEducatorParticipatingCSV,
                      `EducatorsParticipating_${invoiceId}.csv`
                    );
                  }}
                >
                  Educators Participating
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadStudentTeacherGradeCSV,
                      `StudentsByEducator_${invoiceId}.csv`
                    );
                  }}
                >
                  Students by Educator/Grade Level
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadModuleDownloadCSV,
                      `ModuleDownload_${invoiceId}.csv`
                    );
                  }}
                >
                  Module Download
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(
                      API.downloadEdBrowserDownloadCSV,
                      `EdBrowserDownload_${invoiceId}.csv`
                    );
                  }}
                >
                  EdBrowser Download
                </a>
              </li>
            </CSVList>
          </CSVSection>
        </SectionsLayout>
      </Container>
    </>
  );
}
